import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';

type TriggerType = 'click' | 'hover';

/**
 * Компонент дропдаун
 * Построен на базе @angular/cdk/overlay https://material.angular.dev/cdk/overlay/overview
 *
 * trigger='click' - требует явного (click)="dropdown.toggle()" на toggler
 * trigger='hover' - автоматическая логика открытия при наведении
 *
 * Пример использования:
 *  <sd-dropdown #dropdown>
 *    <ng-template #dropdownToggler>
 *      <sd-dropdown-toggler (click)="dropdown.toggle()">
 *        Переключатель
 *      </sd-dropdown-toggler>
 *     </ng-template>
 *     <ng-template #dropdownContent>
 *      <sd-dropdown-menu (click)="dropdown.close()">
 *        <sd-dropdown-menu-item>
 *          Первый пункт
 *        </sd-dropdown-menu-item>
 *         <sd-dropdown-menu-item>
 *          Второй пункт
 *        </sd-dropdown-menu-item>
 *      </sd-dropdown-menu>
 *    </ng-template>
 *  </sd-dropdown>
 */
@Component({
  selector: 'sd-dropdown',
  imports: [NgTemplateOutlet, OverlayModule],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.ngSkipHydration]': 'true',
    '[class.dropdown--opened]': 'opened()',
  },
})
export class DropdownComponent implements AfterViewInit, OnDestroy {
  @ViewChild(CdkOverlayOrigin, { read: ElementRef }) originElement: ElementRef;
  @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  opened = model(false);
  ignoreOutsideClick = input(false);
  positions = input<ConnectedPosition[]>();
  trigger = input<TriggerType>('click');

  dropdownTogglerTemplate = contentChild<TemplateRef<any>>('dropdownToggler');
  dropdownContentTemplate = contentChild<TemplateRef<any>>('dropdownContent');

  private closeTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private overlayListenersAttached = false;
  private originAbortController: AbortController | null = null;
  private overlayAbortController: AbortController | null = null;

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.trigger() === 'hover' && this.originElement?.nativeElement) {
      this.setupHoverListeners();
    }
  }

  ngOnDestroy(): void {
    this.cleanupHoverListeners();
  }

  toggle(): void {
    this.opened.update((opened) => !opened);
  }

  close(): void {
    this.opened.set(false);
  }

  open(): void {
    this.opened.set(true);
  }

  private setupHoverListeners(): void {
    const originEl = this.originElement?.nativeElement;
    if (!originEl) {
      return;
    }

    this.originAbortController?.abort();
    this.originAbortController = new AbortController();

    originEl.addEventListener('mouseenter', () => this.onOriginMouseEnter(), {
      signal: this.originAbortController.signal,
    });
    originEl.addEventListener('mouseleave', () => this.scheduleClose(), {
      signal: this.originAbortController.signal,
    });

    setTimeout(() => this.attachOverlayListeners(), 100);
  }

  private attachOverlayListeners(): void {
    if (this.overlayListenersAttached) {
      return;
    }

    if (!this.overlay?.overlayRef) {
      return;
    }

    const overlayElement = this.overlay.overlayRef.overlayElement;
    if (!overlayElement) {
      return;
    }

    this.overlayAbortController?.abort();
    this.overlayAbortController = new AbortController();

    overlayElement.addEventListener('mouseenter', () => this.clearCloseTimeout(), {
      signal: this.overlayAbortController.signal,
    });
    overlayElement.addEventListener('mouseleave', () => this.scheduleClose(), {
      signal: this.overlayAbortController.signal,
    });

    this.overlayListenersAttached = true;
  }

  private cleanupHoverListeners(): void {
    this.clearCloseTimeout();

    if (this.trigger() === 'hover') {
      this.originAbortController?.abort();
      this.originAbortController = null;

      this.overlayAbortController?.abort();
      this.overlayAbortController = null;

      this.overlayListenersAttached = false;
    }
  }

  private onOriginMouseEnter(): void {
    this.clearCloseTimeout();
    this.open();
    setTimeout(() => this.attachOverlayListeners(), 100);
  }

  private clearCloseTimeout(): void {
    if (this.closeTimeoutId !== undefined) {
      clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = undefined;
    }
  }

  private scheduleClose(): void {
    this.clearCloseTimeout();
    this.closeTimeoutId = setTimeout(() => this.close(), 200);
  }
}
